import * as React from 'react';

import { compose } from 'recompose';

import waitForProps from 'js/lib/waitForProps';

import { applySetting } from 'bundles/internal-enterprise/utils/apiUtils';
import Button from 'bundles/internal-ui/components/buttons/Button';
import Card from 'bundles/internal-ui/components/cards/Card';
import Checkbox from 'bundles/internal-ui/components/forms/Checkbox';
import TextBox from 'bundles/internal-ui/components/forms/TextBox';
import type { InjectedNaptime } from 'bundles/naptimejs';
import Naptime from 'bundles/naptimejs';
import EnterpriseSettingsV1 from 'bundles/naptimejs/resources/enterpriseSettings.v1';
import ThirdPartyOrganizations from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';

import 'css!../__styles__/EnterpriseToolsHome';

const CONSTANT = {
  first: {
    name: 'FIRST_UNENROLLMENT_TRIGGER_ENABLED',
    type: 'booleanContent',
  },
  second: {
    name: 'SECOND_UNENROLLMENT_TRIGGER_ENABLED',
    type: 'booleanContent',
  },
  threshold: {
    name: 'UNENROLLMENT_PROGRESS_THRESHOLD_PERCENTAGE',
    type: 'integerContent',
  },
} as const;

type PropsFromCaller = {
  orgId: string;
};

type Setting = EnterpriseSettingsV1;

type PropsFromNaptime = {
  thirdPartyOrg: { id: string; name: string };
  settings?: Setting[];
  naptime: InjectedNaptime;
};

type Props = PropsFromCaller & PropsFromNaptime;

type State = {
  loading: boolean;
  errorMessage: string;
  successMessage: string;
  toggleFirst: boolean;
  toggleSecond: boolean;
  threshold: number;
};

class OrganizationTriggerSettings extends React.Component<Props, State> {
  constructor(props: $TSFixMe) {
    super(props);
    const { settings } = props;
    const firstTrigger: Setting | false = settings?.find((x: $TSFixMe) => x.id.includes(CONSTANT.first.name)) || false;
    const secondTrigger: Setting | false =
      settings?.find((x: $TSFixMe) => x.id.includes(CONSTANT.second.name)) || false;
    const triggerThreshold: Setting | false =
      settings?.find((x: $TSFixMe) => x.id.includes(CONSTANT.threshold.name)) || false;
    this.state = {
      loading: false,
      errorMessage: '',
      successMessage: '',
      toggleFirst: firstTrigger && (firstTrigger.value as boolean),
      toggleSecond: secondTrigger && (secondTrigger.value as boolean),
      threshold: triggerThreshold ? (triggerThreshold.value as number) : 0,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: $TSFixMe) {
    const { settings } = nextProps;
    const firstTrigger: Setting | false = settings?.find((x: $TSFixMe) => x.id.includes(CONSTANT.first.name)) || false;
    const secondTrigger: Setting | false =
      settings?.find((x: $TSFixMe) => x.id.includes(CONSTANT.second.name)) || false;
    const triggerThreshold: Setting | false =
      settings?.find((x: $TSFixMe) => x.id.includes(CONSTANT.threshold.name)) || false;
    this.setState(() => ({
      toggleFirst: firstTrigger && (firstTrigger.value as boolean),
      toggleSecond: secondTrigger && (secondTrigger.value as boolean),
      threshold: triggerThreshold ? (triggerThreshold.value as number) : 0,
    }));
  }

  onToggleCheckbox = (checkboxId: $TSFixMe) => {
    const { toggleFirst, toggleSecond } = this.state;
    if (checkboxId === 0) {
      this.setState(() => ({
        toggleFirst: !toggleFirst,
        toggleSecond: !toggleFirst && toggleSecond,
      }));
    } else {
      this.setState(() => ({
        toggleFirst: !toggleSecond || toggleFirst,
        toggleSecond: !toggleSecond,
      }));
    }
  };

  onApplyChange = () => {
    const {
      thirdPartyOrg: { id },
      naptime,
    } = this.props;
    const { toggleFirst, toggleSecond, threshold } = this.state;
    const values = {
      first: toggleFirst,
      second: toggleSecond,
      threshold,
    };
    const ids = {
      first: `thirdPartyOrganization!~${id}~${CONSTANT.first.name}`,
      second: `thirdPartyOrganization!~${id}~${CONSTANT.second.name}`,
      threshold: `thirdPartyOrganization!~${id}~${CONSTANT.threshold.name}`,
    };
    this.setState(() => ({
      errorMessage: '',
      successMessage: '',
      loading: true,
    }));

    Object.keys(CONSTANT).forEach((key) => {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const setting = CONSTANT[key];
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      applySetting(ids[key], values[key], setting.type, id).then(
        () => {
          this.setState(() => ({
            successMessage: 'settings applied!',
            loading: false,
          }));
          naptime.refreshData({ resources: ['enterpriseSettings.v1'] });
        },
        (failure) => {
          this.setState(() => ({
            errorMessage: failure.responseJSON,
            loading: false,
          }));
        }
      );
    });
  };

  onUpdateThreshold = (e: $TSFixMe) => {
    this.setState({
      threshold: Math.min(Math.max(Math.round(e.target.value), 0), 100),
    });
  };

  render() {
    const {
      settings,
      thirdPartyOrg,
      thirdPartyOrg: { name },
    } = this.props;
    const { toggleFirst, toggleSecond, threshold, loading, errorMessage, successMessage } = this.state;

    if (!thirdPartyOrg || !settings) {
      return <Card flexibleHeight> settings not available </Card>;
    }

    return (
      <Card flexibleHeight>
        <h3>{name}</h3>
        <Checkbox
          className="theme-green"
          checked={toggleFirst}
          onChange={() => this.onToggleCheckbox(0)}
          style={{ display: 'block' }}
          label="First Trigger"
        />
        <Checkbox
          className="theme-green"
          checked={toggleSecond}
          onChange={() => this.onToggleCheckbox(1)}
          label="Second Trigger"
        />
        <TextBox
          label="Threshold limit in %"
          placeholder="Threshold"
          type="number"
          currentValue={threshold}
          onUpdateField={this.onUpdateThreshold}
          inputProps={{ min: 0, max: 100 }}
        />
        <Button
          onClick={this.onApplyChange}
          text="Apply"
          btnStyle={loading ? 'disabled' : 'success'}
          disabled={loading}
        />
        {errorMessage && <span className="error-message">{errorMessage}</span>}
        {successMessage && <span className="success-message">{successMessage}</span>}
      </Card>
    );
  }
}

export default compose<Props, PropsFromCaller>(
  Naptime.createContainer<PropsFromNaptime, PropsFromCaller>(({ orgId }) => ({
    // @ts-expect-error ts-migrate(2322) FIXME: Type '"" | NaptimeClient<{ id: string; name: strin... Remove this comment to see the full error message
    thirdPartyOrg:
      orgId &&
      ThirdPartyOrganizations.get(orgId, {
        fields: ['name'],
      }),
    settings: orgId
      ? EnterpriseSettingsV1.finder('byOrganization', {
          params: { thirdPartyOrganizationId: orgId, includeProgramSettings: false },
          fields: ['setting'],
        })
      : undefined,
  })),
  waitForProps(['thirdPartyOrg', 'settings'], <Card flexibleHeight>Loading</Card>)
)(OrganizationTriggerSettings);
